import React, { useState } from "react";
import {
    Card,
    CardContent,
    CardMedia,
    InternalStandardProps,
    Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Layout } from "../Layout";
import Grid from "@mui/material/Grid";
import { useNavigate } from "react-router-dom";
import { useRouting } from "JS/React/Hooks/Routes";

import { AppHeader } from "JS/React/Components/AppHeader";
import { Icon, Icons } from "JS/React/Components/Icon";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { messaging } from "JS/Helpers/UserMessaging";
import { AllTabs } from "JS/Models";
import { useGlobalNavStack } from "JS/React/Hooks/NavStack";

const RootDiv = styled("div")(({ theme }) => ({
    padding: theme.spacing(1),
}));

const StyledCard = styled(Card)(({ theme }) => ({
    width: "100%",
    borderRadius: 0,
    display: "flex",
    marginBottom: "15px",
    backgroundColor: theme.palette.background.default,
}));

const StyledCardMedia = styled(CardMedia)({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
});

const StyledCardContent = styled(CardContent)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    color: theme.palette.grey[600],
}));

const CardTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.grey[600],
}));

export interface GiftsProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {}

export enum GiftCategories {
    PURCHASABLE_BY_MEMBERS = "purchase-gifts-for-members",
    PURCHASABLE_BY_PROSPECTS = "purchase-gifts-for-prospects",
    SEND_MEMBER = "send-member-gifts",
    RECEIVED_AS_MEMBER = "received-gifts-as-member",
    RECEIVED_AS_PROSPECT = "received-gifts-as-prospect",
    GIVEN = "gifts-given",
}

export enum GiftContentItemTypes {
    GIFT_MP3 = "GiftMP3",
    GIFT_VIDEO = "GiftVideo",
    GIFT_COURSE = "GiftCourse",
    GIFT_PROS_MP3 = "GiftProsMP3",
    GIFT_PROS_VIDEO = "GiftProsVideo",
}

export const giftCat = {
    [GiftCategories.PURCHASABLE_BY_MEMBERS]: {
        title: "Purchase Gifts for Members",
        icon: Icons.purchaseGiftIcon,
        contentItemTypeName: {
            audio: GiftContentItemTypes.GIFT_MP3,
            video: GiftContentItemTypes.GIFT_VIDEO,
            course: GiftContentItemTypes.GIFT_COURSE,
        },
    },
    [GiftCategories.PURCHASABLE_BY_PROSPECTS]: {
        title: "Purchase Gifts for Prospects",
        icon: Icons.purchaseGiftIcon,
        contentItemTypeName: {
            audio: GiftContentItemTypes.GIFT_PROS_MP3,
            video: GiftContentItemTypes.GIFT_PROS_VIDEO,
        },
    },
    [GiftCategories.SEND_MEMBER]: {
        title: "Send Member Gifts",
        icon: Icons.sendGiftIcon,
        contentItemTypeName: {
            audio: GiftContentItemTypes.GIFT_MP3,
            video: GiftContentItemTypes.GIFT_VIDEO,
            course: GiftContentItemTypes.GIFT_COURSE,
        },
    },
    [GiftCategories.RECEIVED_AS_MEMBER]: {
        title: "Received Gifts as Member",
        icon: Icons.receivedGiftIcon,
    },
    [GiftCategories.RECEIVED_AS_PROSPECT]: {
        title: "Received Gifts as Prospect",
        icon: Icons.receivedGiftIcon,
    },
    [GiftCategories.GIVEN]: {
        title: "Gifts Given",
        icon: Icons.giftGivenIcon,
    },
};

export const Gifts = (props: GiftsProps) => {
    const navigate = useNavigate();

    const { homeStack } = useGlobalNavStack();

    const { linkProvider } = useRouting();

    const { className, ...rest } = props;

    const loading = false;
    const [searchQuery, setSearchQuery] = useState("");

    return (
        <Layout>
            <RootDiv className={className} {...rest}>
                <AppHeader
                    title="Gifts"
                    searchable={false}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    searchPlaceholder={"Search Gifts by title/description"}
                    canGoBack
                    onBackClick={() => {
                        if (homeStack?.isHomeTrack) {
                            navigate(linkProvider.react.home(), { replace: true });
                        } else {
                            navigate(linkProvider.react.more().index(), { replace: true });
                        }
                    }}
                />

                {!loading && (
                    <Grid container spacing={0.2}>
                        {Object.keys(giftCat)?.map((category, idx) => (
                            <StyledCard
                                key={idx}
                                elevation={0}
                                onClick={() =>
                                    category === GiftCategories.GIVEN
                                        ? navigate(
                                              linkProvider.react
                                                  .gifts()
                                                  .giftsGivenListing(category),
                                          )
                                        : navigate(
                                              linkProvider.react
                                                  .gifts()
                                                  .listing(
                                                      category,
                                                      AllTabs.AUDIOS,
                                                  ),
                                          )
                                }
                            >
                                <StyledCardMedia>
                                    <Icon icon={giftCat[category].icon} />
                                </StyledCardMedia>
                                <StyledCardContent>
                                    <CardTitle>
                                        {giftCat[category].title}
                                    </CardTitle>
                                </StyledCardContent>
                            </StyledCard>
                        ))}
                    </Grid>
                )}
                {loading && (
                    <AppBackdropProgress
                        open={true}
                        backdropText={messaging.loader.gift}
                    />
                )}
            </RootDiv>
        </Layout>
    );
};
